import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../api/client';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [signupsPaused, setSignupsPaused] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Check if signups are paused - only run once on mount
  useEffect(() => {
    const checkSignupStatus = async () => {
      try {
        const response = await adminAPI.getSignupStatus();
        const isPaused = response?.data?.data?.signupPaused || false;
        setSignupsPaused(isPaused);
        console.log('Signup status:', isPaused);
      } catch (err) {
        console.error('Error checking signup status:', err);
        setSignupsPaused(false);
      }
    };
    
    checkSignupStatus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: '💱', title: 'Fiat to Crypto', desc: 'Easily convert INR to cryptocurrencies' },
    { icon: '🔐', title: 'Secure Wallet', desc: 'Industry-leading security for your funds' },
    { icon: '⚡', title: 'Fast Transactions', desc: 'Complete payments in minutes' },
    { icon: '📊', title: 'Live Tracking', desc: 'Real-time payment status and balance updates' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      <div className="flex flex-1">
      {/* Left Sidebar */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 bg-gradient-to-br from-slate-900 to-slate-800">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center text-xl font-bold">₹</div>
            <span className="text-2xl font-bold text-white">PaySolutions</span>
          </div>
          <p className="text-gray-400 text-sm">Professional Digital Wallet & Exchange Platform</p>
        </div>

        <div className="space-y-6">
          {features.map((feature, idx) => (
            <div key={idx} className="flex gap-4 items-start">
              <div className="text-3xl">{feature.icon}</div>
              <div>
                <h3 className="text-white font-semibold mb-1">{feature.title}</h3>
                <p className="text-gray-400 text-sm">{feature.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="pt-6 border-t border-gray-700">
          <p className="text-gray-400 text-xs mb-3">Why Choose PaySolutions?</p>
          <ul className="space-y-2 text-sm text-gray-300">
            <li className="flex items-center gap-2"><span className="text-yellow-400">✓</span> 24/7 Customer Support</li>
            <li className="flex items-center gap-2"><span className="text-yellow-400">✓</span> Zero Hidden Charges</li>
            <li className="flex items-center gap-2"><span className="text-yellow-400">✓</span> Multi-Currency Support</li>
            <li className="flex items-center gap-2"><span className="text-yellow-400">✓</span> Instant Withdrawals</li>
          </ul>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-white mb-2">Welcome Back</h2>
            <p className="text-gray-400">Login to access your wallet and manage your assets</p>
          </div>

          {error && (
            <div className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-6 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Username or Email Address</label>
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 text-white placeholder-gray-500 transition"
                placeholder="username or email@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 text-white placeholder-gray-500 transition"
                placeholder="••••••••"
              />
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
                <input type="checkbox" className="rounded bg-slate-700 border-slate-600" />
                Remember me
              </label>
              <Link to="/forgot-password" className="text-yellow-400 hover:text-yellow-300 transition">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-slate-900 font-bold py-3 px-4 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-yellow-400/50"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-700 text-center text-sm">
            <p className="text-gray-400">
              Don't have an account?{' '}
              {signupsPaused ? (
                <span className="text-red-400 font-semibold">🔴 Signups Paused</span>
              ) : (
                <button
                  onClick={() => navigate('/signup')}
                  className="text-yellow-400 hover:text-yellow-300 font-semibold transition bg-none border-none cursor-pointer"
                >
                  Create one now
                </button>
              )}
            </p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};
